/**
 * Studio Feed – local-only feed of published Studio videos (no AT Protocol).
 * Displays videos in a post-like list: video player, title, description.
 */

import {useCallback, useState} from 'react'
import {View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {useInitialNumToRender} from '#/lib/hooks/useInitialNumToRender'
import {type StudioTabNavigatorParams} from '#/lib/routes/types'
import {cleanError} from '#/lib/strings/errors'
import {useStudioPublishedVideosQuery} from '#/state/queries/studio'
import {List, type ListRef} from '#/view/com/util/List'
import {useAnimatedRef} from 'react-native-reanimated'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {ArrowRotateCounterClockwise_Stroke2_Corner0_Rounded as RetryIcon} from '#/components/icons/ArrowRotate'
import {VideoClip_Stroke2_Corner0_Rounded as VideoIcon} from '#/components/icons/VideoClip'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'
import type {StudioVideo} from '#/lib/api/studio'
import {StudioFeedVideoCard} from './StudioFeedVideoCard'

type Props = NativeStackScreenProps<StudioTabNavigatorParams, 'StudioFeed'>

function renderFeedItem({item}: {item: StudioVideo}) {
  return <StudioFeedVideoCard video={item} />
}

function keyExtractor(item: StudioVideo) {
  return item.id
}

export function StudioFeedScreen({navigation}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  // Call useInitialNumToRender unconditionally at top (avoids React #300 when switching to this screen)
  const initialNumToRender = useInitialNumToRender({minItemHeight: 200})
  const scrollRef: ListRef = useAnimatedRef()
  const [isPTRing, setIsPTRing] = useState(false)

  const {
    data: videos = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useStudioPublishedVideosQuery()

  const onRefresh = useCallback(async () => {
    setIsPTRing(true)
    try {
      await refetch()
    } finally {
      setIsPTRing(false)
    }
  }, [refetch])

  return (
    <Layout.Screen testID="studioFeedScreen">
      <Layout.Header.Outer>
        <Layout.Header.MenuButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Studio Feed</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
      </Layout.Header.Outer>
      {videos.length === 0 && !isLoading ? (
        <Layout.Center>
          {isError ? (
            <View style={[a.pt_3xl, a.align_center]}>
              <VideoIcon width={48} fill={t.atoms.text_contrast_low.color} />
              <Text
                style={[a.pt_md, a.pb_sm, a.text_2xl, a.font_semi_bold, t.atoms.text]}>
                <Trans>Whoops!</Trans>
              </Text>
              <Text
                style={[
                  a.text_md,
                  a.pb_xl,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                  {maxWidth: 360},
                ]}>
                {cleanError(error) || _(msg`Failed to load videos`)}
              </Text>
              <Button
                label={_(msg`Retry`)}
                size="small"
                color="secondary_inverted"
                variant="solid"
                onPress={() => refetch()}>
                <ButtonText>
                  <Trans>Retry</Trans>
                </ButtonText>
                <ButtonIcon icon={RetryIcon} position="right" />
              </Button>
            </View>
          ) : (
            <View style={[a.pt_3xl, a.align_center]}>
              <VideoIcon width={48} fill={t.palette.primary_500} />
              <Text
                style={[a.pt_md, a.pb_sm, a.text_2xl, a.font_semi_bold, t.atoms.text]}>
                <Trans>No videos yet</Trans>
              </Text>
              <Text
                style={[
                  a.text_md,
                  a.pb_xl,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                ]}>
                <Trans>Published Studio videos will appear here.</Trans>
              </Text>
            </View>
          )}
        </Layout.Center>
      ) : (
        <List
          ref={scrollRef}
          data={videos}
          renderItem={renderFeedItem}
          keyExtractor={keyExtractor}
          refreshing={isPTRing}
          onRefresh={onRefresh}
          onEndReachedThreshold={1.5}
          initialNumToRender={initialNumToRender}
          windowSize={11}
          desktopFixedHeight
          sideBorders={false}
        />
      )}
    </Layout.Screen>
  )
}
